import React, { useMemo } from 'react';
import { Badge, Card, CardContent } from '@lognai/synapse-design-system-v2';
import { useWorkflowRun } from '../../hooks/use-workflow-run';
import type { WorkflowStepStatus, WorkflowStepView } from './types';

type BadgeTone = 'neutral' | 'success' | 'warning' | 'destructive' | 'info';

const STATUS_TONE: Record<WorkflowStepStatus, BadgeTone> = {
  pending: 'neutral',
  applied: 'success',
  failed: 'destructive',
  skipped: 'neutral',
  undone: 'warning',
  partial: 'warning',
};

const STATUS_LABEL: Record<WorkflowStepStatus, string> = {
  pending: 'Pending',
  applied: 'Applied',
  failed: 'Failed',
  skipped: 'Skipped',
  undone: 'Undone',
  partial: 'Partial',
};

// Derive a friendlier label from a tool name when the draft didn't ship a
// uiLabel of its own. Mirrors ChatMessage.deriveLabel — keeps the user from
// ever seeing raw `ai.draftCreateAllocation`.
const humanizeToolName = (name: string): string => {
  const after = name.includes('.') ? name.slice(name.indexOf('.') + 1) : name;
  const spaced = after.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.length ? spaced[0]!.toUpperCase() + spaced.slice(1) : name;
};

const summarize = (steps: WorkflowStepView[]): string => {
  if (steps.length === 0) return 'No steps yet';
  const total = steps.length;
  const failed = steps.find((s) => s.status === 'failed');
  if (failed) return `failed at step ${failed.index + 1}`;
  const applied = steps.filter((s) => s.status === 'applied').length;
  return `${applied} of ${total} applied`;
};

// Index of the most recently transitioned step (anything past 'pending'). We
// use this for aria-current="step" so screen readers can announce where the
// run currently is. Falls back to the last step when nothing has run yet.
const currentStepIndex = (steps: WorkflowStepView[]): number => {
  let last = 0;
  for (const s of steps) {
    if (s.status !== 'pending') last = s.index;
  }
  return last;
};

export type WorkflowProgressCardVariant = 'compact' | 'expanded';

export interface WorkflowProgressCardProps {
  workflowRunId: string;
  /** When omitted, the component derives from the workflowId on the first draft. */
  workflowDisplayName?: string;
  /** Explicit step list. When omitted, derived from drafts grouped by workflowRunId. */
  steps?: WorkflowStepView[];
  variant?: WorkflowProgressCardVariant;
  /** Hide the header label (used when nested inside a card that already shows the workflow name). */
  hideHeader?: boolean;
}

const DotStep: React.FC<{ step: WorkflowStepView; isCurrent: boolean }> = ({ step, isCurrent }) => (
  <li
    aria-current={isCurrent ? 'step' : undefined}
    data-testid={`workflow-step-dot-${step.index}`}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      fontSize: 11,
      color: 'var(--synapse-text-muted, #6b7280)',
    }}
  >
    <span
      aria-hidden="true"
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background:
          step.status === 'applied'
            ? 'var(--synapse-success, #10b981)'
            : step.status === 'failed'
            ? 'var(--synapse-destructive, #ef4444)'
            : step.status === 'undone' || step.status === 'partial'
            ? 'var(--synapse-warning, #f59e0b)'
            : 'var(--synapse-border, #d1d5db)',
        outline: isCurrent ? '2px solid rgba(99,102,241,0.4)' : 'none',
        outlineOffset: 1,
      }}
    />
    <span>{step.index + 1}</span>
  </li>
);

const ExpandedStep: React.FC<{ step: WorkflowStepView; isCurrent: boolean }> = ({
  step,
  isCurrent,
}) => {
  const label = step.toolLabel ?? humanizeToolName(step.toolName);
  return (
    <li
      aria-current={isCurrent ? 'step' : undefined}
      data-testid={`workflow-step-${step.index}`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '8px 0',
        borderBottom: '1px solid var(--synapse-border, #e5e7eb)',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 22,
          height: 22,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 600,
          background: 'var(--synapse-muted, #f3f4f6)',
          color: 'var(--synapse-text-muted, #6b7280)',
        }}
      >
        {step.index + 1}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
          <Badge tone={STATUS_TONE[step.status]}>{STATUS_LABEL[step.status]}</Badge>
          {step.draftId ? (
            <span
              data-testid={`workflow-step-${step.index}-draftid`}
              style={{
                fontSize: 11,
                color: 'var(--synapse-text-muted, #6b7280)',
                fontFamily: 'ui-monospace, monospace',
              }}
            >
              {step.draftId.slice(0, 8)}
            </span>
          ) : null}
        </div>
        {step.status === 'failed' && step.errorMessage ? (
          <div
            data-testid={`workflow-step-${step.index}-error`}
            style={{
              marginTop: 4,
              fontSize: 12,
              color: 'var(--synapse-destructive, #b91c1c)',
            }}
          >
            {step.errorMessage}
          </div>
        ) : null}
      </div>
    </li>
  );
};

export const WorkflowProgressCard: React.FC<WorkflowProgressCardProps> = ({
  workflowRunId,
  workflowDisplayName,
  steps: stepsFromProps,
  variant = 'expanded',
  hideHeader = false,
}) => {
  const hookResult = useWorkflowRun(stepsFromProps ? null : workflowRunId);

  const steps = stepsFromProps ?? hookResult.steps;
  const resolvedDisplayName =
    workflowDisplayName ?? hookResult.workflowDisplayName ?? hookResult.workflowId ?? workflowRunId;

  const summary = useMemo(() => summarize(steps), [steps]);
  const currentIdx = useMemo(() => currentStepIndex(steps), [steps]);

  const ariaLabel = `Workflow progress: ${resolvedDisplayName}`;

  if (!stepsFromProps && hookResult.isLoading) {
    return (
      <Card style={{ marginTop: 8 }}>
        <CardContent>
          <div
            data-testid="workflow-progress-loading"
            style={{ fontSize: 13, color: 'var(--synapse-text-muted, #6b7280)' }}
          >
            Loading workflow…
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card style={{ marginTop: 8 }}>
      <CardContent>
        <section role="region" aria-label={ariaLabel} data-testid="workflow-progress-card">
          {!hideHeader ? (
            <header
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                marginBottom: variant === 'expanded' ? 8 : 4,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600 }}>{resolvedDisplayName}</div>
              <div
                data-testid="workflow-progress-summary"
                style={{ fontSize: 12, color: 'var(--synapse-text-muted, #6b7280)' }}
              >
                {summary}
              </div>
            </header>
          ) : null}

          {steps.length === 0 ? (
            <div
              data-testid="workflow-progress-empty"
              style={{ fontSize: 12, color: 'var(--synapse-text-muted, #6b7280)' }}
            >
              No steps yet.
            </div>
          ) : variant === 'compact' ? (
            <ol
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              {steps.map((step) => (
                <DotStep key={step.index} step={step} isCurrent={step.index === currentIdx} />
              ))}
            </ol>
          ) : (
            <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {steps.map((step) => (
                <ExpandedStep
                  key={step.index}
                  step={step}
                  isCurrent={step.index === currentIdx}
                />
              ))}
            </ol>
          )}
        </section>
      </CardContent>
    </Card>
  );
};
